import TableErrorBox from 'components/molecules/ErrorBox/TableErrorBox';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CustomTable } from '../PricingSimulator/PricingSimulatorIndexTableWrapper';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import KPISelector from 'components/molecules/TradePromoOptimization/AddEventModal/PromoSections/KPISelector';
import { Button } from 'antd';
import { ProjectOutlined } from '@ant-design/icons';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { promoRecommendationUrls } from 'config/urls';
import { PromoLibraryKpiConfigType, PromoRecommendationKpiConfigType } from 'types/tradePromoOptimization';
import { useFetchToolsLibraryData } from 'hooks/tradePromoOptimization/plans/useFetchToolsLibraryData';
import { UserContext } from 'contexts/ConfigContext';
import { formatNumberWithCommas, getConfig } from 'utils/helperMethods';
import { getPromoRecommendationLibraryTableColumns } from 'utils/getRecommendationTableColumns';
import Footer from 'components/molecules/PricingSimulator/Footer';
import { promoRecommendation } from 'contexts/PromoRecommendationContext';
import { queryClient } from 'services/reactQueryService';

const promoLibraryTableId = 'promo-library-table-id';

type Props = {
    from?: 'promo_library' | 'product_constraint';
    setPromoLibraryKpiConfigData: React.Dispatch<React.SetStateAction<PromoLibraryKpiConfigType | null>>;
    promoLibraryKpiConfigData: PromoLibraryKpiConfigType | null;
};

const PromoRecommendationLibrarySection: FC<Props> = ({
    from,
    promoLibraryKpiConfigData,
    setPromoLibraryKpiConfigData,
}) => {
    const { appConfig } = useContext(UserContext);
    const { selectedPlan } = useContext(promoRecommendation);
    const promoRecommendationConfig = getConfig('promoRecommendation', appConfig);
    const [kpiFilterDropdownValues, setKpiDropdownValues] = useState<any[]>([]);
    const [isKpiFilterVisible, setIsKpiFilterVisible] = useState(false);

    const [promoLibraryTableVariable, setPromoLibraryTableVariable] = useState<any>({
        limit: promoRecommendationConfig?.promoLibraryTableLimit,
        //limit: 20,
        offset: 0,
        kpis: [],
        library_group: from,
        filters: {},
    });
    const tableHeight = useRef<number | null>(0);
    const promoLibraryAttributeTypes = promoRecommendationConfig?.promoLibraryAttributeTypes;

    //this hook with give height to fill the screen height
    tableHeight.current = useFillHeightToFitScreen({
        id: promoLibraryTableId,
        height: tableHeight.current,
        buffer: 110, //footer
    });

    // promo library data
    const {
        loading: promoLibraryTableDataLoading,
        error: promoLibraryTableDataError,
        data: promoLibraryTableData,
        count: promoLibraryTableDataCount,
    } = useFetchToolsLibraryData({
        method: 'post',
        queryConfig: {
            enabled: Boolean(promoLibraryTableVariable?.kpis?.length > 0),
        },
        url: promoRecommendationUrls.promoLibraryTableDataUrl,
        queryKey: [
            'promo-recommendation-promo-library-data',
            'tpo-promo-library-table-data',
            promoLibraryTableVariable,
        ],
        variables: { ...(promoLibraryTableVariable || {}) },
        kpiConfigData: promoLibraryKpiConfigData,
        attributeTypes: promoLibraryAttributeTypes,
    });

    //GET  PROMO LIBRARY KPI CONFIG DATA FROM BACKEND
    const { loading: promoLibraryKPIConfigLoading } = useCommonApiHandler({
        method: 'get',
        queryConfig: {},
        queryKey: ['promo-recommendation-promo-library-kpi-config', from],
        url: `${promoRecommendationUrls.promoLibraryUrl}/${from}`,
        variables: {},
        parserFunction(data: any) {
            const res: PromoRecommendationKpiConfigType = {};
            data?.forEach((item: any) => {
                res[item?.kpi_key] = {
                    pre: item?.pre,
                    post: item?.post,
                    label: item?.kpi_name,
                    allowNegative: item?.allow_negative,
                    isEditable: item?.is_editable,
                    maxValue: item?.max_value,
                    minValue: item?.min_value,
                    type: item?.kpi_type,
                    storageType: item?.storage_type,
                    allowedDecimals: item?.allowed_decimals,
                    allowedValues: item?.allowed_values,
                };
            });
            setPromoLibraryKpiConfigData(res);
            return {};
        },
    });
    useEffect(() => {
        const valueList: any[] = [];
        const updatedList = Object.keys(promoLibraryKpiConfigData ?? {})?.map((configKey) => {
            valueList.push({
                key: configKey,
                label: promoLibraryKpiConfigData?.[configKey]['label'],
                storage_type: promoLibraryKpiConfigData?.[configKey]['storageType'],
            });
            return {
                ...promoLibraryKpiConfigData?.[configKey],
                label: promoLibraryKpiConfigData?.[configKey]['label'],
                value: configKey,
                isSelected: true,
                pre: promoLibraryKpiConfigData?.[configKey]['pre'],
                post: promoLibraryKpiConfigData?.[configKey]['post'],
            };
        });
        setPromoLibraryTableVariable((prev: any) => {
            return {
                ...prev,
                offset: 0,
                kpis: valueList,
                sort_by: promoLibraryAttributeTypes?.[0]?.sortKey,
                sort_order: 'asc',
            };
        });
        setKpiDropdownValues(updatedList);
    }, [promoLibraryKpiConfigData]);

    useEffect(() => {
        setPromoLibraryTableVariable((prev: any) => {
            return {
                ...prev,
                offset: 0,
                library_group: from,
            };
        });
    }, [from]);

    const columns = useMemo(() => {
        if (!promoLibraryTableData) return [];
        const tempColumns = kpiFilterDropdownValues
            ?.filter((item) => item.isSelected)
            ?.map((item) => {
                return {
                    label: item?.label,
                    key: item?.label,
                    kpiInfo: item,
                    sortKey: item?.storageType === 'range' ? false : item?.value,
                };
            });
        const memoisedColumns = getPromoRecommendationLibraryTableColumns(promoLibraryAttributeTypes, tempColumns, {
            sortBy: promoLibraryTableVariable?.sort_by,
            sortOrder: promoLibraryTableVariable?.sort_order === 'asc' ? 'ascend' : 'descend',
        });
        return memoisedColumns;
    }, [promoLibraryTableData]);

    function infiniteScrollOnNext(loading: boolean) {
        if (loading) return;
        const newOffset = promoLibraryTableVariable.offset + promoLibraryTableVariable.limit;
        setPromoLibraryTableVariable({ ...promoLibraryTableVariable, offset: newOffset });
    }

    const commonLoading = promoLibraryTableDataLoading || promoLibraryKPIConfigLoading;

    const isErrorStatus = promoLibraryTableDataError;

    const footerFirstText = `${
        'Showing ' +
        Number(promoLibraryTableData?.length ?? 0)?.toLocaleString() +
        ' of ' +
        formatNumberWithCommas(promoLibraryTableDataCount, 0) +
        ' records'
    }`;
    const footerSecondText = '';
    const footerThirdText = '';
    const footerFourthText = '';

    return (
        <div>
            <div>
                <div className="mb-2 flex justify-end ">
                    <KPISelector
                        buttonComponent={
                            <Button
                                onClick={() => {
                                    setIsKpiFilterVisible(!isKpiFilterVisible);
                                }}
                                icon={<ProjectOutlined />}
                                style={{ borderRadius: 4, margin: 0 }}
                                type="default"
                            >
                                KPI
                            </Button>
                        }
                        isVisible={isKpiFilterVisible}
                        setIsVisible={setIsKpiFilterVisible}
                        kpiList={kpiFilterDropdownValues}
                        onSubmit={(values) => {
                            const updatedList = kpiFilterDropdownValues?.map((item) => {
                                return {
                                    ...item,
                                    isSelected: values.includes(item?.value),
                                };
                            });
                            const updatedValues = kpiFilterDropdownValues
                                ?.filter((kpiItem) => {
                                    return values.includes(kpiItem?.value);
                                })
                                .map((item) => ({ key: item?.value, storage_type: item?.storageType }));
                            setPromoLibraryTableVariable({
                                ...promoLibraryTableVariable,
                                offset: 0,
                                kpis: updatedValues,
                                sort_by:
                                    updatedValues?.find((item) => item?.key === promoLibraryTableVariable?.sort_by)
                                        ?.key ?? promoLibraryAttributeTypes?.[0]?.sortKey,
                                sort_order: promoLibraryTableVariable?.sort_order ?? 'asc',
                            });
                            setKpiDropdownValues(updatedList);
                        }}
                        variableName={'KPI'}
                        hideSearch={true}
                    />
                </div>

                <InfiniteScroller
                    data={promoLibraryTableData}
                    loading={commonLoading}
                    hasMore={promoLibraryTableData?.length < (promoLibraryTableDataCount || 0) || commonLoading}
                    height={
                        promoLibraryTableData?.length && !promoLibraryTableDataError
                            ? tableHeight.current ?? undefined
                            : isNaN(Number(tableHeight.current))
                            ? undefined
                            : Number(tableHeight.current) - 1
                    }
                    //height={200}
                    //     loader={true}
                    onNext={() => infiniteScrollOnNext(commonLoading)}
                >
                    <CustomTable
                        bordered
                        onChange={(pagination, filter, sort: any, extra) => {
                            if (sort?.order) {
                                const sortMapping: any = {
                                    descend: 'desc',
                                    ascend: 'asc',
                                };

                                setPromoLibraryTableVariable({
                                    ...promoLibraryTableVariable,
                                    sort_by: sort?.column?.sortKey,
                                    sort_order: sortMapping?.[sort?.order] || 'asc',
                                    offset: 0,
                                });
                            }
                        }}
                        sticky={{
                            getContainer: () =>
                                document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                        }} //adding x axis scrollbar on table
                        id={promoLibraryTableId}
                        columns={columns}
                        // loading={commonLoading}
                        scroll={{ x: '100%' }}
                        showSorterTooltip={false}
                        dataSource={promoLibraryTableData}
                        pagination={false}
                        style={{
                            opacity: commonLoading ? '0.3' : 1,
                            pointerEvents: commonLoading ? 'none' : 'auto',
                        }}
                        locale={{
                            emptyText: commonLoading ? (
                                <></>
                            ) : promoLibraryTableDataError ? (
                                <TableErrorBox error={promoLibraryTableDataError} height={tableHeight.current} />
                            ) : (
                                ''
                            ),
                        }}
                    />
                </InfiniteScroller>
                <Footer
                    type={isErrorStatus ? 'error' : 'default'}
                    items={[footerFirstText, footerSecondText, footerThirdText, footerFourthText]}
                />
            </div>
        </div>
    );
};

export default PromoRecommendationLibrarySection;
